import EmissionsFactor from "@hydrologiq/shared/monitoring/figures/EmissionsFactor"
import { EnergyPeriod } from "@hydrologiq/common/types/energy/consumption"
import { Emissions as EmissionsType } from "@hydrologiq/gql/graphql"
import { AssetHeadlineFigure } from "@hydrologiq/gql-experience/graphql"
import { isCompactFigure } from "@hydrologiq/shared/figures/generic/figureVariant"

interface EmissionsFactorFigureProps {
  data?: AssetHeadlineFigure
  loading?: boolean
  error?: string
  isPlaceholder?: boolean
  compact?: boolean
}

function EmissionsFactorFigure({ data, loading, error, isPlaceholder, compact = isCompactFigure("monitoring") }: EmissionsFactorFigureProps) {
  // Transform headline figure data to EmissionsFactor component format
  const transformEmissionsData = (headlineData: AssetHeadlineFigure): EmissionsType | undefined => {
    if (!headlineData?.value || !headlineData?.metadata) return undefined

    const emissions: EmissionsType = {
      start: headlineData.lastUpdated || new Date().toISOString(),
    }
    const category = headlineData.metadata.category
    const comparison = headlineData.metadata.comparison?.alternative_fuel

    // Set the current fuel factor based on category
    if (category === "hydrogen") {
      emissions.hydrogen = {
        factor: headlineData.value,
        total: 0, // Not provided in headline data
      }
    } else if (category === "diesel") {
      emissions.diesel = {
        factor: headlineData.value,
        total: 0, // Not provided in headline data
      }
    }

    // Set comparison data if available
    if (comparison && comparison.value && comparison.fuel_type) {
      const comparisonFuel = comparison.fuel_type as "hydrogen" | "diesel"

      if (emissions[comparisonFuel]) {
        emissions[comparisonFuel]!.comparison = {
          factor: comparison.value,
          total: 0, // Not provided in headline data
          fuel: comparisonFuel,
        }
      } else {
        emissions[comparisonFuel] = {
          factor: 0, // Current value for this fuel not available
          total: 0,
          comparison: {
            factor: comparison.value,
            total: 0,
            fuel: comparisonFuel,
          },
        }
      }
    }

    return emissions
  }

  const emissions = data ? transformEmissionsData(data) : undefined

  // Determine period from metadata or default to latest
  const period = data?.metadata?.period ?? EnergyPeriod.LATEST

  return (
    <EmissionsFactor
      period={period}
      emissions={emissions}
      loading={loading}
      error={error}
      title={data?.label}
      isPlaceholder={isPlaceholder}
      compact={compact}
    />
  )
}

export default EmissionsFactorFigure
